<script setup lang="ts">
import { Megaphone } from '@lucide/vue'

import { useCommunicationFormatters } from '../../composables/useCommunicationFormatters'
import type { CommunicationPriority } from '../../types/communication'

defineProps<{
  title: string
  priority: CommunicationPriority
  type: string
  isRead: boolean
  date: string
  actionUrl?: string | null
}>()

const emit = defineEmits<{
  click: []
}>()

const { formatRelativeDate, getPriorityBorderClass } = useCommunicationFormatters()
</script>

<template>
  <div
    :class="[
      'group flex cursor-pointer items-start gap-3 border-l-2 px-3 py-2.5 transition-colors hover:bg-muted/50',
      getPriorityBorderClass(priority),
      isRead ? 'opacity-60' : 'bg-muted/30',
    ]"
    role="button"
    tabindex="0"
    @click="emit('click')"
    @keydown.enter="emit('click')"
  >
    <!-- Megaphone icon -->
    <div
      :class="[
        'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
        isRead ? 'bg-muted' : 'bg-primary/10',
      ]"
    >
      <Megaphone :class="['size-4', isRead ? 'text-muted-foreground' : 'text-primary']" />
    </div>

    <!-- Content -->
    <div class="min-w-0 flex-1">
      <div class="flex items-start justify-between gap-2">
        <p :class="['text-sm', isRead ? 'text-muted-foreground' : 'font-medium text-foreground']">
          {{ title }}
        </p>
        <span class="shrink-0 text-xs text-muted-foreground">
          {{ formatRelativeDate(date) }}
        </span>
      </div>
      <div class="mt-0.5 flex items-center gap-1.5">
        <span
          :class="[
            'inline-block size-1.5 rounded-full',
            priority === 'urgent' ? 'bg-destructive' :
            priority === 'high' ? 'bg-amber-500' :
            'bg-transparent',
          ]"
        />
        <span class="text-[11px] capitalize text-muted-foreground">{{ type }}</span>
      </div>
    </div>
  </div>
</template>
